<template>
	<ui-col :class="getMainClasses" cols="2" style="width: 300px">
		<div class="CustomerPicker__content-container CustomerTree__container">
			<div v-if="$isAllowedTo('tree_view_company_dropdown')">
				<div class="is-flex is-align-items-center">
					<CompanyDropdown ref="compDropdown" v-if="$isAllowedTo('tree_view_company_dropdown')" />

					<ui-menu class="Company-Menu">
						<template #activator="{ on, attrs }">
							<div v-bind="attrs" v-on="on">
								<i class="fas fa-ellipsis-v" style="color: white"/>
							</div>
						</template>
						<div class="Company-MenuItems">
							<div class="Company-MenuItem" @click="showCompanyDialog = true;">
								<span class="icon">
									<i class="fas fa-plus"></i>
								</span>
								<span>Ny Återförsäljare</span>
							</div>
							<div class="Company-MenuItem"  @click="showCompanyDialog = true; isEdit = true;">
								<span class="icon">
									<i class="fas fa-pen"></i>
								</span>
								<span>Editera {{getActiveCompany?.name}}</span>
							</div>
							<div class="Company-MenuItem"  @click="onDeleteCompany">
								<span class="icon">
									<i class="fas fa-trash"></i>
								</span>
								<span>Ta bort {{getActiveCompany?.name}}</span>
							</div>
						</div>
					</ui-menu>
				</div>

				<hr style="height: 1px; background-color: #4d535d;"/>
			</div>

			<ui-alert v-if="error" error>{{ error }}</ui-alert>


			<div class="mb-4 is-flex is-justify-content-flex-end">
				<ui-btn 
					v-if="$isAllowedTo('tree_add_new_customer')"
					class="ml-2" 
					icon="fa-plus" 
					small 
					@click="newCustomer"
				>
					Ny Kund
				</ui-btn>
			</div>

			<CustomSelect
				:options="getSearchResult"
				:placeholder="'Sök nivå..'"
				@select="setActiveCustomer"
				@search="searchCustomer"
			/>

			<ui-progress v-if="isPending" class="mt-5"/>

			<template v-else>
				<CustomerTree
					:items="customers"
					:open-items="getOpenItems"
					:readonly="!$isAllowedTo('tree_modify_level')"
					@new-level="onNewLevel"
					@edit-level="onEditLevel"
					@move-level="onMoveLevel"
					@remove-level="onRemoveLevel"
				/>

				<label 
					class="has-text-white is-size-7 mt-6" 
					v-if="externalCustomers.length > 0"
				>
					Kopplade kunder
				</label>
				<CustomerTree
					:items="externalCustomers"
					readonly
				/>
			</template>

		</div>

		<CustomerFormDialog
			v-if="showCustomerDialog"
			:is-edit="isEdit"
			:is-root="isRoot"
			@close="onCustomerDialogClose"
			@on-update="fetchTree"
		/>

		<CompanyFormDialog
			v-if="showCompanyDialog"
			:is-edit="isEdit"
			@close="onCompanyDialogClose"
			@on-update="onCompanyUpdate"
		/>

		<DeleteModal
			v-if="showCompanyDeleteModal || showCustomerDeleteModal"
			:message="deleteMessage"
			:error="deleteError"
			@confirm="showCompanyDeleteModal ? onConfirmDeleteCompany() : onConfirmDeleteCustomer()"
			@cancel="onCancelDelete"
		/>

		<CustomerMoveDialog
			v-if="showCustomerMoveDialog"
			@close="showCustomerMoveDialog = false"
			@on-update="onCustomerMoveUpdate"
		/>
	</ui-col>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import CustomerMoveDialog from '@/components/customer/customer-move-dialog';
import CustomerFormDialog from '@/components/customer/customer-form-dialog';
import CompanyFormDialog from '@/components/company/company-form-dialog';
import DeleteModal from '../DeleteModal.vue';
import CompanyDropdown from '@/components/company/company-dropdown';
import CustomerTree from '@/components/customer/customer-tree';
import CustomSelect from '@/components/CustomSelect';

export default {
	components: {
		CompanyDropdown,
		CustomerTree,
		CustomerFormDialog,
		CustomSelect,
		CompanyFormDialog,
		CustomerMoveDialog,
		DeleteModal
	},

	data: () => ({
		isPending: false,
		error: '',
		customers: [],
		externalCustomers: [],

		searchQuery: '',

		showCustomerDialog: false,
		isEdit: false,
		isRoot: false,

		showCompanyDialog: false,
		showCustomerMoveDialog: false,

		showCompanyDeleteModal: false,
		showCustomerDeleteModal: false,
		deleteMessage: '',
		deleteError: ''
	}),

	computed: {
		...mapGetters('appStore', ['getCustomerPicker', 'isMobile']),
		...mapGetters('user', ['getActiveCustomer', 'getActiveCompany', 'getUserCustomer', 'getUser']),
		...mapGetters('companyStore', ['companies']),

		getMainClasses() {
			return ['CustomerPicker', {
				'CustomerPicker--hidden': this.getCustomerPicker === false,
				'CustomerPicker--mobile': this.isMobile
			}];
		},

		getOpenItems() {
			if (!this.getActiveCustomer) {
				return [];
			}

			return this.$utils.tree.getPathIds(this.customers, this.getActiveCustomer.id);
		},

		getSearchResult() {
			if (this.searchQuery.length === 0) {
				return [];
			}

			return this.$utils.tree.toFlat(this.customers).filter(customer => customer.name.toLowerCase().includes(this.searchQuery.toLowerCase()));
		}
	},

	watch: {
		'getActiveCompany.id'() {
			this.fetchTree();
		}
	},

	created() {
		this.fetchTree();
	},

	methods: {
		...mapActions('user', ['setActiveCustomer', 'setActiveCompany']),

		searchCustomer(query) {
			this.searchQuery = query;
		},

		onCustomerDialogClose() {
			this.isEdit = false;
			this.isRoot = false;
			this.showCustomerDialog = false;
		},

		onCompanyDialogClose() {
			this.isEdit = false;
			this.showCompanyDialog = false;
		},

		onNewLevel() {
			this.showCustomerDialog = true;
		},

		onEditLevel() {
			this.isEdit = true;
			this.showCustomerDialog = true;
		},

		onRemoveLevel() {
			this.deleteMessage = `Är du säker på att du vill ta bort ${this.getActiveCustomer.name}`;
			this.showCustomerDeleteModal = true;
		},

		onMoveLevel() {
			this.showCustomerMoveDialog = true;
		},

		onDeleteCompany() {
			this.deleteMessage = `Är du säker på att du vill ta bort ${this.getActiveCompany?.name}`;
			this.showCompanyDeleteModal = true;
		},

		async onConfirmDeleteCompany() {
			try {
				await this.$services.company.deleteCompany(this.getActiveCompany?.id, {
					isLoading: val => {
						this.isLoading = val;
					}
				});
			} catch (error) {
				this.deleteError = error.message;

				return;
			}

			this.showCompanyDeleteModal = false;

			this.setActiveCustomer(null);
			let companies = await this.onCompanyUpdate();
			this.$refs.compDropdown.setActiveCompany(companies[0]);
		},

		async onConfirmDeleteCustomer() {
			try {
				await this.$services.customer.deleteCustomer(this.getActiveCustomer.id, {
					isLoading: val => {
						this.isPending = val;
					}
				});
			} catch (error) {
				this.deleteError = error.message;

				return;
			}

			this.showCustomerDeleteModal = false;

			await this.fetchTree();
			this.setActiveCustomer(this.customers.length > 0 ? this.customers[0] : null);
		},

		onCancelDelete() {
			this.deleteMessage = '';
			this.deleteError = '';
			this.showCompanyDeleteModal = false;
			this.showCustomerDeleteModal = false;
		},

		newCustomer() {
			this.isRoot = true;
			this.showCustomerDialog = true;
		},

		onCustomerMoveUpdate() {
			this.fetchTree();
			this.$bus.$emit('active-customer:fetch');
		},

		async onCompanyUpdate() {
			return await this.$refs.compDropdown.refetchCompanies();
		},

		async fetchTree() {
			this.error = '';
			let rootNode = null;

			if (!this.$isAllowedTo('tree_view_full') && this.$isAllowedTo('tree_see_own_root')) {
				rootNode = await this.$services.customer.getRootCustomer({ 
					customerId: this.getUser.customerId, companyId: this.getActiveCompany.id 
				}, {
					loading: val => this.isPending = val
				});
			}

			try {
				this.customers = await this.$services.customer.getTree({
					companyNode: this.getActiveCompany?.id,
					parentNode: this.$isAllowedTo('tree_view_full')
						? null
						: rootNode
							? rootNode.id
							: this.getUser.customerId
				}, {
					loading: val => {
						this.isPending = val;
					}
				});

				this.externalCustomers = await this.$services.customer.getExternalCustomersOnUser(this.getUser.id, {
					loading: val => {
						this.isPending = val;
					}
				});
			} catch (error) {
				this.error = error.message;
			}
		}
	}
};
</script>

<style lang="scss">
.CustomerPicker {
	margin-top: 48px !important;
	background-color: #363a41;
}

.CustomerPicker--hidden {
	display: none !important;
}

.CustomerPicker--mobile {
	z-index: 8;
	width: 70vw !important;
	height: 100%;
	position: fixed;
}


.CustomerPicker__content-container {
	max-height: 100%;
	display: flex;
	flex-direction: column;
}

.Company-Menu {
	cursor: pointer;
	display: flex;
	margin-left: 5px;
	border-radius: 3px;
	justify-content: center;
	width: 30px;
	background-color: #363a41;
	transition: background-color 0.3s ease;

	.ui-menu-activator {
		width: 100%;
		height: 100%;
		display: flex;
		justify-content: center;
		align-items: center;
	}


	&:hover {
		transition: background-color 0.3s ease;
		background-color: lighten(#363a41, 10%);
	}
}

.Company-MenuItems {
	min-width: 200px;
}

.Company-MenuItem {
	cursor: pointer;
	font-size: 12px;
	background-color: white;
	border-radius: 3px;
	margin-bottom: 2px;
	transition: background-color 0.3s ease;


	&:hover {
		transition: background-color 0.3s ease;
		background-color: darken(white, 10%);
	}
}

.Company-MenuItem:last-child {
	margin-bottom: 0px;
}
</style>
